// Iter52 P9 — Universal banner showing POD-vs-no-POD totals + per-category-value
// breakdown for any import source (SF / CRM / Easy).
//
// Helps the user spot misclassification risks BEFORE running an import — e.g.
// a new record-type appearing in a Salesforce export that the importer doesn't
// know how to classify.
//
// Used by both the Preflight dialog and the SF Simulate (dry-run) dialog.
// If `onMapUnknown` is provided, clicking on an unknown category chip / row
// invokes it (admin-only click-to-map flow).

import { AlertTriangle, CheckCircle2, MapPin, Plus, Sparkles } from "lucide-react";

const KIND_STYLES = {
  EE:         { bg: "bg-amber-100",   text: "text-amber-800",    border: "border-amber-300",   label: "EE" },
  Gaz:        { bg: "bg-blue-100",    text: "text-blue-800",     border: "border-blue-300",    label: "Gaz" },
  VAS:        { bg: "bg-emerald-100", text: "text-emerald-800",  border: "border-emerald-300", label: "VAS" },
  HVAS:       { bg: "bg-teal-100",    text: "text-teal-800",     border: "border-teal-300",    label: "HVAS" },
  // Iter52 P13g — Combo offers (utility + bundled VAS service) — labels match Products page
  "Combo EE + VAS":  { bg: "bg-indigo-100", text: "text-indigo-800", border: "border-indigo-300", label: "Combo EE + VAS" },
  "Combo Gaz + VAS": { bg: "bg-cyan-100",   text: "text-cyan-800",   border: "border-cyan-300",   label: "Combo Gaz + VAS" },
  Necunoscut: { bg: "bg-slate-100",   text: "text-slate-700",    border: "border-slate-300",   label: "?" },
};

export function ImportCategoryBanner({ data, onMapUnknown }) {
  if (!data || (!data.pod_breakdown && !data.category_breakdown)) return null;

  const pod = data.pod_breakdown || {};
  const cats = data.category_breakdown || [];
  const unknownCats = data.unknown_categories || [];
  const hasUnknown = unknownCats.length > 0;
  const categoryLabel = data.category_label || "Categorie";
  const categoryColumn = data.category_column;
  const isClickable = typeof onMapUnknown === "function";

  return (
    <div
      className="rounded-lg border border-indigo-200 bg-indigo-50/40 p-3 space-y-3"
      data-testid="import-category-banner"
    >
      <div className="flex items-center gap-2">
        <MapPin className="h-4 w-4 text-indigo-700" />
        <h4 className="text-sm font-semibold text-indigo-900">
          Conținut fișier · POD vs. fără POD &amp; breakdown per «{categoryLabel}»
        </h4>
      </div>

      {/* POD vs no-POD summary */}
      <div className="grid grid-cols-3 gap-2 text-sm">
        <div
          className="rounded-md border border-emerald-200 bg-emerald-50 p-2 text-center"
          data-testid="pod-with"
        >
          <p className="text-xs text-emerald-700">Cu POD</p>
          <p className="text-xl font-bold text-emerald-700 font-mono">
            {pod.with_pod ?? 0}
          </p>
        </div>
        <div
          className="rounded-md border border-amber-200 bg-amber-50 p-2 text-center"
          data-testid="pod-without"
        >
          <p className="text-xs text-amber-700">Fără POD</p>
          <p className="text-xl font-bold text-amber-700 font-mono">
            {pod.without_pod ?? 0}
          </p>
        </div>
        <div
          className="rounded-md border border-slate-200 bg-white p-2 text-center"
          data-testid="pod-total"
        >
          <p className="text-xs text-slate-600">Total rânduri</p>
          <p className="text-xl font-bold text-slate-800 font-mono">
            {pod.total ?? 0}
          </p>
        </div>
      </div>
      {pod.pod_column ? (
        <p className="text-[11px] text-slate-500 -mt-1.5">
          Coloana POD detectată: <span className="font-mono">{pod.pod_column}</span>
        </p>
      ) : (
        <p className="text-[11px] text-amber-700 -mt-1.5">
          ⚠ Nu am găsit nicio coloană POD în fișier.
        </p>
      )}

      {/* Unknown categories warning (key value-add!) */}
      {hasUnknown && (
        <div
          className="rounded-md border border-rose-300 bg-rose-50 p-2.5 text-xs text-rose-900 flex items-start gap-2"
          data-testid="unknown-categories-warning"
        >
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-rose-600" />
          <div>
            <p className="font-semibold">
              {unknownCats.length} valori «{categoryLabel}» nu pot fi clasificate automat
            </p>
            <p className="text-[11px] mt-0.5">
              Rândurile cu aceste valori vor primi clasificarea default (EE pentru rânduri cu POD,
              skip pentru cele fără POD). Adaugă regulile lipsă în clasificatorul importer-ului
              pentru a evita misclassification.
            </p>
            <div className="mt-1.5 flex flex-wrap gap-1">
              {unknownCats.slice(0, 8).map((v, i) => {
                const baseClass = "inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white border border-rose-300 font-mono text-[11px]";
                if (isClickable) {
                  return (
                    <button
                      key={i}
                      type="button"
                      onClick={() => onMapUnknown(v)}
                      className={`${baseClass} hover:bg-rose-100 hover:border-rose-400 cursor-pointer transition`}
                      data-testid={`map-unknown-${i}`}
                    >
                      <Plus className="h-3 w-3" />
                      {v}
                    </button>
                  );
                }
                return (
                  <span key={i} className={baseClass}>
                    {v}
                  </span>
                );
              })}
              {unknownCats.length > 8 && (
                <span className="text-[11px] text-rose-700">
                  …+{unknownCats.length - 8} mai multe
                </span>
              )}
            </div>
            {isClickable && unknownCats.length > 0 && (
              <p className="text-[11px] mt-1.5 text-rose-700 italic">
                💡 Click pe o valoare pentru a o mapa instant la EE/Gaz/VAS/HVAS — fără cod.
              </p>
            )}
          </div>
        </div>
      )}

      {/* Category table */}
      {cats.length > 0 ? (
        <div className="rounded-md border border-slate-200 overflow-hidden">
          {categoryColumn ? (
            <p className="text-[11px] text-slate-500 px-2.5 py-1 bg-slate-50 border-b border-slate-200">
              Coloana «{categoryLabel}» detectată:{" "}
              <span className="font-mono">{categoryColumn}</span>
            </p>
          ) : (
            <p className="text-[11px] text-amber-700 px-2.5 py-1 bg-amber-50 border-b border-amber-200">
              ⚠ Nu am găsit o coloană «{categoryLabel}» — verifică headerele.
            </p>
          )}
          <div className="max-h-56 overflow-y-auto">
            <table className="w-full text-sm" data-testid="category-table">
              <thead className="bg-slate-50 text-xs text-slate-600 sticky top-0">
                <tr>
                  <th className="text-left px-2.5 py-1.5 font-medium">Valoare</th>
                  <th className="text-center px-2.5 py-1.5 font-medium">Clasificare</th>
                  <th className="text-right px-2.5 py-1.5 font-medium">Rânduri</th>
                </tr>
              </thead>
              <tbody>
                {cats.slice(0, 100).map((c, i) => {
                  const style = KIND_STYLES[c.detected_kind] || KIND_STYLES.Necunoscut;
                  const fromRule = c.source === "rule";
                  const canMap = isClickable && !c.known;
                  return (
                    <tr
                      key={i}
                      className={`border-t border-slate-100 hover:bg-slate-50/50 ${canMap ? "cursor-pointer" : ""}`}
                      onClick={canMap ? () => onMapUnknown(c.value) : undefined}
                      data-testid={`category-row-${i}`}
                    >
                      <td className="px-2.5 py-1.5 font-mono text-[12px]">
                        {c.value}
                      </td>
                      <td className="px-2.5 py-1.5 text-center">
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${style.bg} ${style.text} ${style.border}`}
                          title={fromRule ? "Clasificat de o regulă admin" : (c.known ? "Clasificare automată" : "Necunoscut")}
                        >
                          {fromRule ? (
                            <Sparkles className="h-3 w-3" />
                          ) : c.known ? (
                            <CheckCircle2 className="h-3 w-3" />
                          ) : (
                            <AlertTriangle className="h-3 w-3" />
                          )}
                          {style.label}
                          {fromRule && <span className="text-[9px] uppercase tracking-wide ml-0.5">regulă</span>}
                        </span>
                      </td>
                      <td className="text-right px-2.5 py-1.5 font-mono font-semibold">
                        {c.count}
                        {canMap && (
                          <span className="ml-2 text-[10px] text-indigo-600 font-sans">↳ mapează</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {cats.length > 100 && (
            <p className="text-[11px] text-slate-500 px-2.5 py-1 bg-slate-50 border-t border-slate-200">
              Afișate primele 100 din {cats.length} valori (restul agregate).
            </p>
          )}
        </div>
      ) : (
        categoryColumn === null && (
          <p className="text-[11px] text-amber-700">
            ⚠ Nicio coloană «{categoryLabel}» găsită — fișierul s-ar putea să nu aibă tipuri de
            înregistrare clasificabile.
          </p>
        )
      )}
    </div>
  );
}

export default ImportCategoryBanner;
